import { useState } from "react";
import { api } from "../api";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface Vote {
  userId: number;
  name: string;
  v: number;
}

interface View {
  role: "leader" | "guesser";
  leader: string;
  scale: { l: string; r: string };
  hasClue: boolean;
  clue?: string;
  target?: number;
  v?: number;
  diff?: number;
  votes?: Vote[];
}

const COPY = {
  not_leader: "Хвилю сьогодні задає хтось інший",
  leader_clues: "Ти сьогодні задаєш хвилю, не вгадуєш",
  no_clue_yet: "Ведучий ще думає над підказкою",
  bad_clue: "Підказка: 2-100 символів",
  clue_has_number: "Без цифр у підказці 🙃",
  bad_value: "Постав повзунок і надішли",
};

function ScaleBar({ scale, marks }: { scale: { l: string; r: string }; marks: { at: number; label: string; tone: "target" | "me" | "other" }[] }) {
  return (
    <div className="mt-6">
      <div className="relative h-2 rounded-full bg-gradient-to-r from-[#5b8def] via-[rgba(128,128,128,0.35)] to-[#ef5b5b]">
        {marks.map((m, i) => (
          <div key={i} className="absolute -top-5 flex flex-col items-center" style={{ left: `${m.at}%`, transform: "translateX(-50%)" }}>
            <span className="text-[10px] leading-none text-tghint">{m.label}</span>
            <span
              className={`mt-0.5 block h-6 w-1.5 rounded-full ${
                m.tone === "target" ? "bg-ok" : m.tone === "me" ? "bg-tgbutton" : "bg-[rgba(128,128,128,0.7)]"
              }`}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs font-semibold">
        <span>← {scale.l}</span>
        <span>{scale.r} →</span>
      </div>
    </div>
  );
}

export function Khvylia({ date }: { date?: string | undefined }) {
  const g = useGame<View>("khvylia", COPY, date);
  const [clue, setClue] = useState("");
  const [v, setV] = useState(50);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const view = s.view;
  const won = s.status === "won";
  const isLeader = view.role === "leader";

  const revealMarks: { at: number; label: string; tone: "target" | "me" | "other" }[] = [];
  if (view.target !== undefined) revealMarks.push({ at: view.target, label: "🎯", tone: "target" });
  if (view.v !== undefined) revealMarks.push({ at: view.v, label: "ти", tone: "me" });
  for (const vote of view.votes ?? []) {
    if (view.v !== undefined && vote.v === view.v) continue;
    revealMarks.push({ at: vote.v, label: vote.name.split(" ")[0] ?? "", tone: "other" });
  }

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ХВИЛЯ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-3">
        <div className="mt-1 text-center text-xs text-tghint">
          {isLeader ? "ти сьогодні задаєш хвилю 🌊" : `хвилю задає ${view.leader}`}
        </div>

        {isLeader ? (
          <>
            <div className="mt-3 rounded-2xl bg-tgsection px-4 py-3 text-center">
              <div className="text-[11px] uppercase tracking-wide text-tghint">таємна точка</div>
              <div className="mt-1 text-3xl font-extrabold">{view.target}</div>
              <div className="text-xs text-tghint">0 = {view.scale.l} · 100 = {view.scale.r}</div>
            </div>
            <ScaleBar scale={view.scale} marks={revealMarks} />
            {view.hasClue ? (
              <div className="mt-6 rounded-2xl bg-tgbutton/15 px-4 py-3 text-center">
                <div className="text-[15px] font-semibold">«{view.clue}»</div>
                <div className="mt-1 text-xs text-tghint">підказка пішла в чат - дивись, куди їх винесе</div>
              </div>
            ) : (
              <div className="mt-6">
                <input
                  value={clue}
                  onChange={(e) => setClue(e.target.value)}
                  maxLength={100}
                  placeholder="одна річ/поняття, що сидить рівно в цій точці"
                  autoComplete="off"
                  className="w-full rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 py-3 text-[16px] text-tgtext outline-none placeholder:text-[rgba(128,128,128,0.6)] focus:border-tgbutton"
                />
                <button
                  type="button"
                  onClick={() => void g.run(() => api.guess<View>("khvylia", `clue:${clue.trim()}`, date))}
                  disabled={g.busy || clue.trim().length < 2}
                  className="mt-2 w-full rounded-xl bg-tgbutton py-3 text-sm font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
                >
                  {g.busy ? "…" : "задати хвилю 🌊"}
                </button>
              </div>
            )}
          </>
        ) : !view.hasClue ? (
          <div className="mt-10 text-center text-sm text-tghint">
            {view.leader} ще думає над підказкою 🌊<br />
            зайди трохи пізніше
          </div>
        ) : (
          <>
            <div className="mt-3 rounded-2xl bg-tgsection px-4 py-3 text-center">
              <div className="text-[11px] uppercase tracking-wide text-tghint">підказка</div>
              <div className="mt-1 text-xl font-bold">«{view.clue}»</div>
            </div>

            {!finished ? (
              <>
                <ScaleBar scale={view.scale} marks={[{ at: v, label: "ти", tone: "me" }]} />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={v}
                  onChange={(e) => setV(Number(e.target.value))}
                  className="mt-4 w-full accent-[var(--tg-theme-button-color,#2ea6ff)]"
                />
                <div className="mt-1 text-center text-2xl font-extrabold tabular-nums">{v}</div>
                <button
                  type="button"
                  onClick={() => void g.run(() => api.guess<View>("khvylia", `v:${v}`, date))}
                  disabled={g.busy}
                  className="mt-3 w-full rounded-xl bg-tgbutton py-3 text-sm font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
                >
                  {g.busy ? "…" : "ставлю тут"}
                </button>
                <div className="mt-2 text-center text-[11px] text-tghint">точка відкриється після твоєї відповіді</div>
              </>
            ) : (
              <>
                <ScaleBar scale={view.scale} marks={revealMarks} />
                <div className="mt-8 text-center">
                  <div className="text-3xl font-extrabold">{view.diff === 0 ? "🎯 в яблучко!" : `±${view.diff}`}</div>
                  <div className="mt-1 text-xs text-tghint">
                    точка була {view.target} · ти поставив {view.v}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={isLeader ? "Хвиля пішла!" : won && view.diff !== undefined && view.diff <= 5 ? "Серфер 🌊" : "Записано"}
          subtitle={
            isLeader
              ? "тепер дивись, куди їх винесе"
              : `±${view.diff ?? "?"} від точки · стрік ${s.me.streak} 🔥`
          }
          scoreLabel="avg ±"
          formatScore={(n) => `±${n ?? "–"}`}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
